import { useEffect, useState } from "react";
import { MdPerson } from "react-icons/md";
import { useParams } from "react-router-dom";
import CustomerService from "../../rest/customerService";
import OrderService from "../../rest/orderService";
import OrderInfo from "../../components/orderInfo";
import TopNavBar from "../../components/reusables/topNavBar";
import AssetsService from "../../rest/assetsService";
import { formatCurrency } from "../../utils/CurrencyUtils";
import { formatString } from "../../utils/StringUtils";
import ClientService from "../../rest/clientService";
import { HistoryList } from "../../components/HistoryList";
import { toast } from "react-toastify";

function StudentPage() {
    const { searchInput } = useParams();
    const viewerRole = sessionStorage.getItem('role');
    const isTurnsReadOnly = viewerRole === 'STUDENT' || viewerRole === 'PARENT';
    const [client, setClient] = useState(null);
    const [parenting, setParenting] = useState([]);
    const [orders, setOrders] = useState([]);
    const [order, setOrder] = useState(null);
    const [imageData, setImageData] = useState(null);
    const [showOrderInfo, setShowOrderInfo] = useState(false);
    const [allTurns, setAllTurns] = useState([]);
    const [preferredTurnIds, setPreferredTurnIds] = useState([]);
    const [savingTurns, setSavingTurns] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const clientData = await CustomerService.getSpecificStudent(searchInput);
                setClient(clientData);
            } catch (error) { console.error(error); }
        };

        fetchData();
    }, [searchInput]);

    useEffect(() => {
        if (client && client.login) {
            const fetchClientDetails = async () => {
                try {
                    const [parentResult, orderResult, rechargeResult, expensesResult] = await Promise.allSettled([
                        CustomerService.findParentsByStudent(client.id_student),
                        OrderService.getAllOrdersFromClient(client.login),
                        OrderService.getAllCreditRechargesFromClient(client.login),
                        OrderService.getAllExpensesForClient(client.login),
                    ]);

                    if (parentResult.status === "fulfilled") setParenting(parentResult.value);

                    const safeOrders = orderResult.status === "fulfilled" && Array.isArray(orderResult.value) ? orderResult.value : [];
                    const safeRecharges = rechargeResult.status === "fulfilled" && Array.isArray(rechargeResult.value) ? rechargeResult.value : [];
                    const safeExpenses = expensesResult.status === "fulfilled" && Array.isArray(expensesResult.value) ? expensesResult.value : [];

                    setOrders([...safeOrders, ...safeRecharges, ...safeExpenses]);
                } catch (error) { console.error(error); }
            }
            fetchClientDetails();

            const fetchProfileImage = async () => {
                try {
                    const preferences = await ClientService.getClientAdminPreferences();
                    if (preferences?.enable_profile_pictures === true && client.image_url) {
                        const response = await AssetsService.getProfileImage(client.image_url);
                        setImageData(response);
                    }
                } catch (error) { console.error('Error fetching profile image:', error); }
            };
            fetchProfileImage();

            const fetchTurnPreferences = async () => {
                try {
                    const [turns, prefs] = await Promise.all([
                        ClientService.getTurns(),
                        CustomerService.getStudentTurnPreferences(client.login),
                    ]);
                    setAllTurns(turns || []);
                    setPreferredTurnIds(prefs || []);
                } catch (error) { console.error('Error fetching turn preferences:', error); }
            };
            fetchTurnPreferences();
        }
    }, [client]);

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            return dateString;
        }
    };

    const resolveStatusBadge = (order) => {
        if (order.transaction_type === 'Credit Recharge Asaas' && order.asaasStatus) {
            const map = { CONFIRMED: ['Confirmado', 'bg-green-100 text-green-700'], FAILED: ['Falhou', 'bg-red-100 text-red-700'], PENDING: ['Pendente', 'bg-yellow-100 text-yellow-700'] };
            const [label, cls] = map[order.asaasStatus] || ['Pendente', 'bg-yellow-100 text-yellow-700'];
            return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${cls}`}>{label}</span>;
        }
        if (order.status) {
            const map = {
                FINISHED:          ['Finalizado',              'bg-green-100 text-green-700'],
                COMPLETED:         ['Pago',                    'bg-blue-100 text-blue-700'],
                READYTODELIVER:    ['Pronto p/ Entrega',       'bg-purple-100 text-purple-700'],
                PROCESSINGPAYMENT: ['Aguard. Pagamento',       'bg-yellow-100 text-yellow-700'],
                CANCELED:          ['Cancelado',               'bg-red-100 text-red-700'],
            };
            const [label, cls] = map[order.status] || [null, null];
            if (label) return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${cls}`}>{label}</span>;
        }
        return null;
    };

    const renderOrderItem = (order) => (
        <div className="flex justify-between items-center w-full">
            <div>
                <p className={`font-semibold text-gray-800 ${order.status === 'CANCELED' ? 'text-red-500 line-through' : ''}`}>
                    {order.items_list
                        ? formatString(order.items_list)
                        : order.transaction_type === 'Credit Recharge Admin' ? "RECARGA CRÉDITO" : order.transaction_type === 'Credit Recharge Asaas' ? "RECARGA DE SALDO" : "CORREÇÃO"}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-gray-500">{formatDateTime(order.purchase_date || order.transactionDate)}</p>
                    {resolveStatusBadge(order)}
                </div>
            </div>
            <div className="text-right">
                <p className={`font-bold ${order.status === 'CANCELED' ? 'text-red-500' : 'text-custom_blue'}`}>
                    {order.total ? formatCurrency(order.total) : formatCurrency(order.value)}
                </p>
            </div>
        </div>
    );

    const handleToggleTurn = (turnId) => {
        setPreferredTurnIds(prev =>
            prev.includes(turnId) ? prev.filter(id => id !== turnId) : [...prev, turnId]
        );
    };

    const handleSaveTurnPreferences = async () => {
        setSavingTurns(true);
        try {
            await CustomerService.updateStudentTurnPreferences(client.login, preferredTurnIds);
            toast.success("Turnos atualizados com sucesso!");
        } catch (error) {
            console.error(error);
            toast.error("Erro ao atualizar turnos.");
        } finally {
            setSavingTurns(false);
        }
    };

    const InfoCard = ({ title, children, className }) => (
        <div className={`bg-white rounded-2xl shadow-sm border border-slate-200 p-6 ${className}`}>
            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b border-slate-100 pb-2">{title}</h3>
            {children}
        </div>
    );

    const DetailRow = ({ label, value }) => (
        <div className="flex justify-between items-start py-2 border-b border-slate-100 last:border-0">
            <span className="text-sm font-medium text-gray-500 shrink-0 mr-4">
                {label}
            </span>

            <span className="text-sm font-semibold text-gray-800 text-right break-words break-all flex-1">
                {value || "-"}
            </span>
        </div>
    );

    return (
        <div className='h-screen flex flex-col bg-slate-50 font-poppins'>
            <TopNavBar title="Aluno" />

            {client ? (
                <div className='flex-1 overflow-y-auto p-4 lg:p-8'>
                    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="space-y-6">
                            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col items-center text-center">
                                <div className="w-32 h-32 rounded-full bg-slate-100 border-4 border-white shadow-md overflow-hidden mb-4">
                                    {imageData ? (
                                        <img src={imageData} alt={client.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                            <MdPerson size={64} />
                                        </div>
                                    )}
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800">{client.name}</h2>
                                <span className="inline-block bg-blue-100 text-custom_blue text-xs font-bold px-3 py-1 rounded-full mt-2">
                                    {client.grade || "Sem série"}
                                </span>

                                <div className="w-full mt-6 grid grid-cols-2 gap-4">
                                    <div className="bg-green-50 p-3 rounded-xl border border-green-100">
                                        <p className="text-xs text-green-600 font-bold uppercase">Saldo</p>
                                        <p className="text-xl font-bold text-green-700">{formatCurrency(client.credit)}</p>
                                    </div>
                                    <div className="bg-orange-50 p-3 rounded-xl border border-orange-100">
                                        <p className="text-xs text-orange-600 font-bold uppercase">Emergência</p>
                                        <p className="text-xl font-bold text-orange-700">{client.credit <= 0 ? formatCurrency(client.emergency_value + client.credit) : formatCurrency(client.emergency_value)}</p>
                                    </div>
                                </div>
                            </div>

                            <InfoCard title="Limites e Configurações">
                                <DetailRow label="Limite Diário" value={formatCurrency(client.spend_limit)} />
                                <DetailRow label="Limite de Emergência" value={formatCurrency(client.emergency_value)} />
                                <DetailRow label="Aluno Pagante" value={client.isPayingStudent ? "Sim" : "Não"} />
                                <DetailRow label="Status" value={client.user_status ? "Ativo" : "Inativo"} />
                            </InfoCard>

                            <InfoCard title="Turnos de Refeição">
                                {allTurns.length === 0 ? (
                                    <p className="text-sm text-gray-400 text-center py-2">Nenhum turno cadastrado.</p>
                                ) : isTurnsReadOnly ? (
                                    <div className="space-y-2">
                                        {allTurns.map(turn => {
                                            const checked = preferredTurnIds.includes(turn.turnId);
                                            return (
                                                <div
                                                    key={turn.turnId}
                                                    className="flex items-center gap-3 py-2 px-3 rounded-lg border border-slate-100 bg-slate-50"
                                                >
                                                    <div className={`w-4 h-4 rounded flex items-center justify-center border ${checked ? 'bg-custom_blue border-custom_blue' : 'border-gray-300 bg-white'}`}>
                                                        {checked && <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-semibold text-gray-800">{turn.name}</p>
                                                        <p className="text-xs text-gray-400">{turn.startTime || turn.start} – {turn.endTime || turn.end}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-xs text-gray-400 mb-3">
                                            Selecione os turnos em que este aluno faz refeições. Turnos sem seleção indicam que o aluno pode usar qualquer turno.
                                        </p>
                                        <div className="space-y-2">
                                            {allTurns.map(turn => {
                                                const checked = preferredTurnIds.includes(turn.turnId);
                                                return (
                                                    <label
                                                        key={turn.turnId}
                                                        className="flex items-center gap-3 py-2 px-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => handleToggleTurn(turn.turnId)}
                                                            className="w-4 h-4 accent-custom_blue"
                                                        />
                                                        <div className="flex-1">
                                                            <p className="text-sm font-semibold text-gray-800">{turn.name}</p>
                                                            <p className="text-xs text-gray-400">{turn.startTime || turn.start} – {turn.endTime || turn.end}</p>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <button
                                            onClick={handleSaveTurnPreferences}
                                            disabled={savingTurns}
                                            className="mt-4 w-full bg-custom_blue hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
                                        >
                                            {savingTurns ? "Salvando..." : "Salvar Turnos"}
                                        </button>
                                    </>
                                )}
                            </InfoCard>
                        </div>

                        <div className="lg:col-span-2 space-y-6">

                            <InfoCard title="Dados de Contato">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <DetailRow label="Email" value={client.email} />
                                    <DetailRow label="Telefone" value={client.phone_number} />
                                    <DetailRow label="Responsável 1" value={parenting[0]?.name} />
                                    <DetailRow label="Responsável 2" value={parenting[1]?.name} />
                                </div>
                            </InfoCard>

                            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 h-[500px] flex flex-col">
                                <HistoryList
                                    title="Histórico de Transações"
                                    data={orders}
                                    renderItem={renderOrderItem}
                                    onItemClick={(selected) => { setOrder(selected); setShowOrderInfo(true); }}
                                    className="flex-1 overflow-y-auto"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                    <p>Carregando dados do aluno...</p>
                </div>
            )}

            {order && (
                <OrderInfo
                    order={order}
                    showOrderInfoModal={showOrderInfo}
                    handleCancelClick={() => setShowOrderInfo(false)}
                    customer={client}
                />
            )}
        </div>
    );
}

export default StudentPage;